import { navigate } from '../routing/router';

/**
 * Options for creating a navigation link
 */
export interface ALinkOptions {
  href: string;
  text: string;
  className?: string;
  id?: string;
  title?: string;
  target?: string;
  replaceState?: boolean;
  onClick?: (event: MouseEvent) => void;
}

/**
 * Creates a SPA-style navigation link that prevents full page reloads
 * @param options - Configuration options for the link
 * @returns HTMLAnchorElement configured for SPA navigation
 */
export function createALink(options: ALinkOptions): HTMLAnchorElement {
  const link = document.createElement('a');
  
  // Set basic attributes
  link.href = options.href;
  link.textContent = options.text;
  
  if (options.className) {
    link.className = options.className;
  }
  
  if (options.id) {
    link.id = options.id;
  }
  
  if (options.title) {
    link.title = options.title;
  }
  
  if (options.target) {
    link.target = options.target;
  }
  
  // Add click handler for SPA navigation
  link.addEventListener('click', (event: MouseEvent) => {
    // Allow external links, new tabs, and modified clicks to work normally
    const isExternalLink = link.host !== window.location.host;
    const isNewTab = link.target === '_blank';
    const isModifiedClick = event.ctrlKey || event.shiftKey || event.metaKey || event.button !== 0;
    
    if (isExternalLink || isNewTab || isModifiedClick) {
      return; // Let browser handle it normally
    }
    
    // Call custom onClick handler if provided
    if (options.onClick) {
      options.onClick(event);
    }
    
    // Prevent default browser navigation
    event.preventDefault();
    
    // Use SPA navigation
    navigate(options.href, options.replaceState || false);
  });
  
  return link;
}

/**
 * Helper function to create a link and append it to a container
 * @param container - The container element to append the link to
 * @param options - Configuration options for the link
 * @returns The created HTMLAnchorElement
 */
export function appendALink(container: HTMLElement, options: ALinkOptions): HTMLAnchorElement {
  const link = createALink(options);
  container.appendChild(link);
  return link;
}

/**
 * Converts existing anchor tags in a container to SPA-style navigation links
 * Only affects internal links (same host)
 * @param container - The container element containing anchor tags
 */
export function convertLinksToSPA(container: HTMLElement): void {
  const links = container.querySelectorAll('a');
  
  links.forEach(link => {
    // Skip if already has a click handler or is external
    const isExternalLink = link.host !== window.location.host;
    const isNewTab = link.target === '_blank';
    
    if (isExternalLink || isNewTab) {
      return; // Keep external links as-is
    }
    
    // Add SPA navigation behavior
    link.addEventListener('click', (event: MouseEvent) => {
      const isModifiedClick = event.ctrlKey || event.shiftKey || event.metaKey || event.button !== 0;
      
      if (isModifiedClick) {
        return; // Let browser handle modified clicks normally
      }
      
      event.preventDefault();
      navigate(link.pathname, false);
    });
  });
}
